import * as React from "react";
import { AlertTriangle, Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import type { FormVariant } from "../types";
import { setVariantInfoBlockTestId, setVariantInfoBlockOverrideTestId } from "./variant-info-block.utils";

export interface VariantInfoBlockProps {
  variant: FormVariant;
  onOverrideBagSize: () => void;
  onOverrideGrind: () => void;
  overriddenGrinds?: string[];
  overriddenBagSize?: string;
  isOverrideGrindEnabled?: boolean;
  isOverrideBagSizeEnabled?: boolean;
  detectedInvalidGrinds: string[];
}

interface AttributeBlockProps {
  hasOverride: boolean | undefined;
  hasDetectedAttribute: boolean | undefined;
  attributeName: string;
  attributeValue?: string;
  overriddenValues?: string[] | string;
  handleOverride: () => void;
  overrideTestId?: string;
}

const AttributeBlock = ({
  handleOverride,
  hasDetectedAttribute,
  hasOverride,
  attributeName,
  attributeValue,
  overriddenValues,
  overrideTestId,
}: AttributeBlockProps) => {
  const { t } = useTranslation();

  const displayValue = React.useMemo(() => {
    if (!overriddenValues) return attributeValue;
    return overriddenValues;
  }, [attributeValue, overriddenValues]);

  const badgeVariant = React.useMemo(() => {
    return hasOverride ? "secondary" : "default";
  }, [hasOverride]);

  if (!hasDetectedAttribute) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 rounded-md border p-3">
      <div className="text-sm font-medium text-muted-foreground">{`${attributeName}:`}</div>
      <div className="flex flex-wrap gap-1.5">
        {Array.isArray(displayValue) &&
          displayValue.map((value) => (
            <Badge key={value} variant={badgeVariant}>
              {value}
            </Badge>
          ))}
        {!Array.isArray(displayValue) && displayValue && <Badge variant={badgeVariant}>{displayValue}</Badge>}
      </div>
      <Button type="button" size="sm" variant="outline" onClick={handleOverride} className="ml-auto" data-testid={overrideTestId}>
        {hasOverride ? (
          <>
            <Check className="size-4" />
            <span>{t("components.SingleVariantForm.reset")}</span>
          </>
        ) : (
          <>
            <X className="size-4" />
            <span>{t("components.SingleVariantForm.override")}</span>
          </>
        )}
      </Button>
    </div>
  );
};

export const VariantInfoBlock = ({
  variant,
  onOverrideBagSize,
  onOverrideGrind,
  overriddenGrinds,
  overriddenBagSize,
  isOverrideGrindEnabled = false,
  isOverrideBagSizeEnabled = false,
  detectedInvalidGrinds,
}: VariantInfoBlockProps) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3" data-testid={setVariantInfoBlockTestId("root")}>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t("components.SingleVariantForm.attributesBlockTitle")}</h3>
        {detectedInvalidGrinds.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              {t("views.shopifyImportModal.productForm.variants.invalidGrindsWarning", {
                grinds: detectedInvalidGrinds.join(", "),
              })}
            </AlertDescription>
          </Alert>
        )}
      </div>
      <div className="space-y-2">
        {isOverrideBagSizeEnabled && (
          <AttributeBlock
            handleOverride={onOverrideBagSize}
            hasDetectedAttribute={variant?.hasDetectedBagSize}
            hasOverride={variant?.hasDetectedBagSizeOverride}
            attributeName={t("components.ProductForm.bagSize")}
            overriddenValues={overriddenBagSize}
            attributeValue={variant.mapDetectedBagSizeTo || variant.bagSize}
            overrideTestId={setVariantInfoBlockOverrideTestId("bag-size")}
          />
        )}
        {isOverrideGrindEnabled && (
          <AttributeBlock
            handleOverride={onOverrideGrind}
            hasDetectedAttribute={variant.hasDetectedGrindType}
            hasOverride={variant.hasDetectedGrindOverride}
            attributeName={t("components.ProductForm.grind")}
            attributeValue={variant.grind?.[0]}
            overriddenValues={overriddenGrinds}
            overrideTestId={setVariantInfoBlockOverrideTestId("grind")}
          />
        )}
      </div>
    </div>
  );
};
